/**
 * Pestañas accesibles (patrón WAI-ARIA tabs) para la descripción de producto Elife.
 * Sin JS el contenido se ve completo; con JS se muestra la lista de pestañas y solo el panel
 * activo. Flechas izquierda/derecha, Inicio y Fin mueven entre pestañas.
 */
class ElifeTabs extends HTMLElement {
  /** @type {HTMLElement | null} */
  #tablist = null;

  connectedCallback() {
    this.#tablist = this.querySelector('[role="tablist"]');
    if (!this.#tablist || this.#tabs.length === 0) return;

    this.#tablist.hidden = false;
    this.dataset.enhanced = '';
    this.#tablist.addEventListener('click', this.#handleClick);
    this.#tablist.addEventListener('keydown', this.#handleKeydown);

    const selected = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.#tabs[0];
    if (selected) this.#select(selected, false);
  }

  disconnectedCallback() {
    this.#tablist?.removeEventListener('click', this.#handleClick);
    this.#tablist?.removeEventListener('keydown', this.#handleKeydown);
  }

  /** @returns {HTMLElement[]} */
  get #tabs() {
    return this.#tablist ? Array.from(this.#tablist.querySelectorAll('[role="tab"]')) : [];
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (tab instanceof HTMLElement) this.#select(tab, false);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const tabs = this.#tabs;
    const current = tabs.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (current === -1) return;

    const isRtl = getComputedStyle(this).direction === 'rtl';
    let next = -1;
    if (event.key === 'ArrowRight') next = isRtl ? current - 1 : current + 1;
    else if (event.key === 'ArrowLeft') next = isRtl ? current + 1 : current - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    const target = tabs[(next + tabs.length) % tabs.length];
    if (target) this.#select(target, true);
  };

  /**
   * @param {HTMLElement} selectedTab
   * @param {boolean} focus
   */
  #select(selectedTab, focus) {
    for (const tab of this.#tabs) {
      const isSelected = tab === selectedTab;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls') ?? '');
      if (panel) panel.hidden = !isSelected;
    }
    if (focus) selectedTab.focus();
  }
}

if (!customElements.get('elife-tabs')) {
  customElements.define('elife-tabs', ElifeTabs);
}
